import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Calendar, Clock, MapPin, Video, Users, Plus, Trash2, Edit3, ArrowLeft,
  CheckCircle2, XCircle, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGE = {
  scheduled:  'bg-blue-100 text-blue-800',
  completed:  'bg-emerald-100 text-emerald-800',
  cancelled:  'bg-gray-200 text-gray-700',
};

const Meetings = ({ user }) => {
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [meetings, setMeetings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tab, setTab] = useState('upcoming');
  const [filterProject, setFilterProject] = useState('all');
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    project_id: '', title: '', agenda: '',
    scheduled_at: '', duration_min: 60,
    location: '', meeting_url: '',
    attendees: [], status: 'scheduled', notes: '',
  });
  const [newAttendee, setNewAttendee] = useState({ name: '', email: '', role: '' });
  const [saving, setSaving] = useState(false);

  // True if user is an admin or PM (can manage any project they see in this list).
  // For social-actor / investor / team-member, manage rights are per-project (owner check)
  // — the backend is the source of truth and will return 403 if not allowed.
  const isElevated = ['administrator', 'admin', 'project_manager'].includes(user?.role);

  // Per-meeting check: can the current user manage THIS meeting's project?
  const canManageMeeting = (m) => {
    if (isElevated) return true;
    const proj = projects.find((p) => p.id === m.project_id);
    if (!proj) return false;
    return proj.owner_id === user?.id || proj.project_manager_id === user?.id;
  };

  // For the "Schedule Meeting" header button — show if user can manage at least one project
  const canScheduleAny = isElevated || projects.some(
    (p) => p.owner_id === user?.id || p.project_manager_id === user?.id
  );

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [m, p] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/meetings`, { headers }),
        axios.get(`${BACKEND_URL}/api/projects`, { headers }),
      ]);
      setMeetings(m.data || []);
      setProjects(p.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const nowIso = useMemo(() => new Date().toISOString(), []);
  const filtered = useMemo(() => {
    let list = meetings;
    if (filterProject !== 'all') list = list.filter((m) => m.project_id === filterProject);
    if (tab === 'upcoming') list = list.filter((m) => m.scheduled_at >= nowIso && m.status !== 'cancelled');
    else if (tab === 'past') list = list.filter((m) => m.scheduled_at < nowIso);
    else if (tab === 'cancelled') list = list.filter((m) => m.status === 'cancelled');
    return list.sort((a, b) => {
      const av = a.scheduled_at || '';
      const bv = b.scheduled_at || '';
      return tab === 'upcoming' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [meetings, tab, filterProject, nowIso]);

  const openCreate = () => {
    setEditing(null);
    const defaultProject = projects[0]?.id || '';
    setForm({
      project_id: defaultProject, title: '', agenda: '',
      scheduled_at: '', duration_min: 60,
      location: '', meeting_url: '',
      attendees: [], status: 'scheduled', notes: '',
    });
    setNewAttendee({ name: '', email: '', role: '' });
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      project_id: m.project_id || '',
      title: m.title || '',
      agenda: m.agenda || '',
      scheduled_at: (m.scheduled_at || '').slice(0, 16), // datetime-local format
      duration_min: m.duration_min || 60,
      location: m.location || '',
      meeting_url: m.meeting_url || '',
      attendees: Array.isArray(m.attendees) ? m.attendees : [],
      status: m.status || 'scheduled',
      notes: m.notes || '',
    });
    setNewAttendee({ name: '', email: '', role: '' });
    setDialogOpen(true);
  };

  const addAttendee = () => {
    const a = { ...newAttendee };
    a.name = (a.name || '').trim();
    if (!a.name) { toast.error('Attendee name is required'); return; }
    setForm((f) => ({ ...f, attendees: [...f.attendees, a] }));
    setNewAttendee({ name: '', email: '', role: '' });
  };

  const removeAttendee = (idx) => {
    setForm((f) => ({ ...f, attendees: f.attendees.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.project_id) { toast.error('Project is required'); return; }
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.scheduled_at) { toast.error('Scheduled date/time is required'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_min: parseInt(form.duration_min, 10) || 60,
      };
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/meetings/${editing.id}`, body, { headers });
        toast.success('Meeting updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/meetings`, body, { headers });
        toast.success('Meeting scheduled');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save meeting');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete meeting "${m.title}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/meetings/${m.id}`, { headers });
      toast.success('Meeting deleted');
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete meeting');
    }
  };

  const setStatus = async (m, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/meetings/${m.id}`, { status }, { headers });
      toast.success(`Meeting marked as ${status}`);
      fetchAll();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const fmtWhen = (iso, dur) => {
    if (!iso) return '';
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${date} • ${time} • ${dur || 60} min`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-1">
              <ArrowLeft className="h-3 w-3 mr-1" /> {t('common.back') || 'Back'}
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Calendar className="h-6 w-6 mr-2 text-blue-600" /> Schedule Meetings
            </h1>
            <p className="text-sm text-gray-500">Plan, track, and run meetings tied to your projects</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="w-[220px]" data-testid="meetings-project-filter">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canScheduleAny && (
              <Button onClick={openCreate} data-testid="schedule-meeting-btn">
                <Plus className="h-4 w-4 mr-2" /> Schedule Meeting
              </Button>
            )}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">Past</TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled">Cancelled</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="space-y-3">
            {loading ? (
              <Card><CardContent className="py-10 text-center text-gray-500">Loading meetings…</CardContent></Card>
            ) : filtered.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-gray-500">
                <Calendar className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>No meetings here yet</p>
                {canScheduleAny && (
                  <Button variant="outline" size="sm" onClick={openCreate} className="mt-3">
                    <Plus className="h-4 w-4 mr-1" /> Schedule one
                  </Button>
                )}
              </CardContent></Card>
            ) : (
              filtered.map((m) => (
                <Card key={m.id} data-testid={`meeting-card-${m.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium">{m.title}</h3>
                          <Badge className={STATUS_BADGE[m.status] || 'bg-gray-100'}>{m.status}</Badge>
                          {m.project_title && (
                            <Link to={`/projects/${m.project_id}`} className="text-xs text-blue-600 hover:underline inline-flex items-center">
                              <ExternalLink className="h-3 w-3 mr-1" />{m.project_title}
                            </Link>
                          )}
                        </div>
                        <div className="flex items-center text-xs text-gray-500 gap-3 flex-wrap mb-2">
                          <span className="inline-flex items-center"><Clock className="h-3 w-3 mr-1" />{fmtWhen(m.scheduled_at, m.duration_min)}</span>
                          {m.location && (
                            <span className="inline-flex items-center"><MapPin className="h-3 w-3 mr-1" />{m.location}</span>
                          )}
                          {m.meeting_url && (
                            <a href={m.meeting_url} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center text-blue-600 hover:underline">
                              <Video className="h-3 w-3 mr-1" />Join link
                            </a>
                          )}
                          {Array.isArray(m.attendees) && m.attendees.length > 0 && (
                            <span className="inline-flex items-center"><Users className="h-3 w-3 mr-1" />{m.attendees.length} attendee{m.attendees.length === 1 ? '' : 's'}</span>
                          )}
                        </div>
                        {m.agenda && <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">{m.agenda}</p>}
                        {m.notes && (
                          <div className="mt-2 bg-gray-50 border rounded p-2 text-xs text-gray-700 whitespace-pre-line">
                            <span className="font-medium text-gray-600">Notes: </span>{m.notes}
                          </div>
                        )}
                      </div>
                      {canManageMeeting(m) && (
                        <div className="flex flex-col gap-1.5">
                          {m.status === 'scheduled' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => setStatus(m, 'completed')} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Mark complete
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setStatus(m, 'cancelled')} className="text-gray-600">
                                <XCircle className="h-3 w-3 mr-1" /> Cancel
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openEdit(m)}>
                            <Edit3 className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => remove(m)} className="text-red-600 border-red-300 hover:bg-red-50">
                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Meeting' : 'Schedule a Meeting'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Project *</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                  <SelectTrigger data-testid="meeting-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g., Sprint Planning" data-testid="meeting-title-input" />
              </div>
              <div>
                <Label>Date & Time *</Label>
                <Input type="datetime-local" value={form.scheduled_at}
                       onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} data-testid="meeting-datetime-input" />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input type="number" min="5" max="600" value={form.duration_min}
                       onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))} />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Conference Room A or 'Online'" />
              </div>
              <div>
                <Label>Meeting URL</Label>
                <Input value={form.meeting_url} onChange={(e) => setForm((f) => ({ ...f, meeting_url: e.target.value }))} placeholder="https://meet…" />
              </div>
              <div className="col-span-2">
                <Label>Agenda</Label>
                <Textarea rows={3} value={form.agenda} onChange={(e) => setForm((f) => ({ ...f, agenda: e.target.value }))} placeholder="What will be discussed?" />
              </div>
              {editing && (
                <>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Notes (post-meeting)</Label>
                    <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Decisions, action items…" />
                  </div>
                </>
              )}
            </div>

            {/* Attendees */}
            <div className="border rounded p-3 space-y-2">
              <Label>Attendees</Label>
              <div className="grid grid-cols-12 gap-2">
                <Input className="col-span-4" placeholder="Name" value={newAttendee.name} onChange={(e) => setNewAttendee({ ...newAttendee, name: e.target.value })} />
                <Input className="col-span-4" placeholder="Email" value={newAttendee.email} onChange={(e) => setNewAttendee({ ...newAttendee, email: e.target.value })} />
                <Input className="col-span-3" placeholder="Role" value={newAttendee.role} onChange={(e) => setNewAttendee({ ...newAttendee, role: e.target.value })} />
                <Button type="button" size="sm" className="col-span-1" onClick={addAttendee} data-testid="add-attendee-btn">
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              {form.attendees.length > 0 && (
                <div className="space-y-1 pt-2">
                  {form.attendees.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded">
                      <span>
                        <span className="font-medium">{a.name}</span>
                        {a.email && <span className="text-gray-500 ml-1">&lt;{a.email}&gt;</span>}
                        {a.role && <Badge variant="outline" className="ml-2 text-xs">{a.role}</Badge>}
                      </span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeAttendee(i)}>
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="meeting-save-btn">
                {saving ? 'Saving…' : (editing ? 'Update' : 'Schedule')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Meetings;
